<template>
  <v-dialog
    content-class="dialog"
    max-width="fit-content"
    persistent
    v-bind:value="value"
    v-on:input="onInput"
  >
    <v-card class="dialog__wrapper">
      <v-card-title class="text-h7 dialog__title">
        {{ title }}
      </v-card-title>
      <v-card-actions class="center dialog__buttons">
        <button
          class="dialog__button"
          type="button"
          v-bind:class="leftButton.class"
          v-on:click="onClickLeftButton"
        >{{ leftButton.title }}</button>
        <button
          class="dialog__button"
          type="button"
          v-bind:class="rightButton.class"
          v-on:click="onClickRightButton"
        >{{ rightButton.title }}</button>
        <button
          class="dialog__cross"
          v-bind:class="crossButton.class"
          type="button"
          v-bind:title="crossButton.title"
          v-on:click="onClickCrossButton"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

export const DialogResult = {
  ACCRUE: 'accrue',
  CANCEL: 'cancel',
  CHANGE: 'change',
  DELETE: 'delete',
  DELETE_ACCENT: 'delete-accent',
  CROSS: 'cross-close',
  NO: 'no',
  NOT: 'not',
  OK: 'ok',
  YES: 'yes',
  YES_APPLY: 'yes-apply',
};

/**
 * @description Примесь встраиваемого диалога.
 * @type {Object}
 */
export const builtInDialogMixin = {
  inject: ['showDialog'],
  methods: {
    /**
     * @description Показать диалог подтверждения.
     * @param {string} title Заголовок.
     * @returns {Promise<{result:string}>}
     */
    showAcceptDialog(title) {
      return this.showDialog(title, DialogResult.YES, DialogResult.NO);
    },
    /**
     * @description Показать диалог удаления.
     * @param {string} title Заголовок.
     * @returns {Promise<{result:string}>}
     */
    showRemoveDialog(title) {
      return this.showDialog(title, DialogResult.CANCEL, DialogResult.DELETE);
    },
    /**
     * @description Показать альтернативный диалог удаления.
     * @param {string} title Заголовок.
     * @returns {Promise<{result:string}>}
     */
    showRemoveAccentDialog(title) {
      return this.showDialog(title, DialogResult.NOT, DialogResult.DELETE_ACCENT);
    },
    /**
     * @description Показать диалог начисления.
     * @param {string} title Заголовок.
     * @returns {Promise<{result:string}>}
     */
    showAccrueDialog(title) {
      return this.showDialog(title, DialogResult.CANCEL, DialogResult.ACCRUE);
    },
  },
};

/**
 * @description Примесь диалога.
 * @type {Object}
 */
export const dialogMixin = {
  data() {
    return {
      dialogLeftButtonType: DialogResult.CANCEL,
      dialogRightButtonType: DialogResult.DELETE,
      dialogCrossButton: DialogResult.CROSS,
      dialogTitle: '',
      isShownDialog: false,
      dialogResolve: () => {},
    };
  },
  methods: {
    onCloseDialog(result) {
      this.dialogResolve({ result });
      this.isShownDialog = false;
    },
  },
  provide() {
    return {
      showDialog: (
        title,
        leftButtonType = DialogResult.CANCEL,
        rightButtonType = DialogResult.DELETE,
        crossButtonClose = DialogResult.CROSS,
      ) => new Promise(
        (resolve) => {
          this.isShownDialog = true;
          this.dialogTitle = title;
          this.dialogLeftButtonType = leftButtonType;
          this.dialogRightButtonType = rightButtonType;
          this.dialogCrossButton = crossButtonClose;
          this.dialogResolve = resolve;
        },
      ),
    };
  },
};

const Buttons = {
  [DialogResult.ACCRUE]: { title: 'Начислить', class: DialogResult.ACCRUE },
  [DialogResult.CANCEL]: { title: 'Отмена', class: '' },
  [DialogResult.CHANGE]: { title: 'Да, изменить', class: 'negative' },
  [DialogResult.DELETE]: { title: 'Удалить', class: 'negative' },
  [DialogResult.DELETE_ACCENT]: { title: 'Да, удалить', class: 'negative' },
  [DialogResult.CROSS]: { title: 'Закрыть окно', class: '' },
  [DialogResult.NO]: { title: 'Нет', class: 'negative' },
  [DialogResult.NOT]: { title: 'Нет', class: '' },
  [DialogResult.OK]: { title: 'Ок', class: '' },
  [DialogResult.YES]: { title: 'Да', class: '' },
  [DialogResult.YES_APPLY]: { title: 'Да, применить', class: '' },
};

const NAME_EVENT_CLOSE_DIALOG = 'close-dialog';
const NAME_EVENT_INPUT = 'input';

/**
 * @description Компонент диалога.
 * @type {Vue}
 */
export default {
  computed: {
    leftButton() {
      return Buttons[this.leftButtonType];
    },
    rightButton() {
      return Buttons[this.rightButtonType];
    },
    crossButton() {
      return Buttons[this.crossButtonClose];
    },
  },
  props: {
    leftButtonType: { required: true, type: String },
    rightButtonType: { required: true, type: String },
    crossButtonClose: { required: true, type: String },
    title: { required: true, type: String },
    value: { required: true, type: Boolean },
  },
  methods: {
    /**
     * @description Обработать нажатие на левую кнопку.
     * @returns {void}
     */
    onInput(value) {
      this.$emit(NAME_EVENT_INPUT, value);
    },
    /**
     * @description Обработать нажатие на левую кнопку.
     * @returns {void}
     */
    onClickLeftButton() {
      this.$emit(NAME_EVENT_CLOSE_DIALOG, this.leftButtonType);
    },
    /**
     * @description Обработать нажатие на правую кнопку.
     * @returns {void}
     */
    onClickRightButton() {
      this.$emit(NAME_EVENT_CLOSE_DIALOG, this.rightButtonType);
    },
    /**
     * @description Обработать нажатие на крестик.
     * @returns {void}
     */
    onClickCrossButton() {
      this.$emit(NAME_EVENT_CLOSE_DIALOG, this.crossButtonClose);
    },
  },
  name: 'ClinicPanelDialog',
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";
.dialog {
  &__wrapper {
    padding-inline: 32px;
    padding-block-start: 14px;
    max-width: 800px;
  }
  &__title {
    max-width: 450px;
    margin-inline: auto;
    margin-bottom: 10px;
    padding-inline: 14px;
    word-break: auto-phrase;
    white-space: pre-line;
    overflow-wrap: anywhere;
    hyphens: auto;
    text-align: center;
  }
  &__buttons {
    display: flex;
    flex-flow: row wrap;
    gap: 10px;
    padding: 8px 16px 30px !important;
  }
  &__button {
    @include buttons.btn-transparent(fit-content, 2rem, 1rem);
    min-width: 150px;
    padding: 0 10px;
    &.negative {
      border: 1px solid rgba(224, 4, 4, 0.97);
      color: rgba(224, 4, 4, 0.97);
    }
    &.accrue {
      color: #ffffff;
      background-color: #0489E0;
      &:hover {
        color: #ffffff;
        background-color: #0076C4;
      }
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
  }
  &__cross {
    position: absolute;
    right: 15px;
    top: 15px;
    width: 17px;
    height: 17px;
    background: url("@/assets/img/exit-black.svg") no-repeat 50%;
    background-size: contain;
    transition: background-image var(--animate-duration);
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/exit-blue.svg");
    }
  }
}
.center {
  justify-content: center;
}

@media screen and (max-width: 480px) {
  .dialog {
    .v-card > .v-card__title {
      padding-inline: 10px;
      font-size: 18px;
    }
  }
}

@media screen and (max-width: 400px) {
  .dialog {
    &__button {
      width: 100%;
    }
  }
}
</style>
